import * as React from "react";
import { Handle, Position, type NodeProps } from "@xyflow/react";
import { useHistory } from "react-router-dom";
import { ArrowRight, Rocket } from "lucide-react";

import { cn } from "@/lib/utils";
import { WORKSPACE_ACCOUNT_CARD_INNER_SIZE, WORKSPACE_NODE_CARD_HEIGHT, WORKSPACE_NODE_CARD_WIDTH } from "../workspaceGraphConstants";
import {
  WORKSPACE_EXECUTING_ACCOUNT_DIAMOND_OVERHANG,
  WORKSPACE_EXECUTING_ACCOUNT_DIAMOND_SIZE,
  WORKSPACE_EXECUTING_ACCOUNT_NODE_HEIGHT,
  WORKSPACE_EXECUTING_ACCOUNT_NODE_WIDTH,
} from "../workspaceExecutingAccountUtils";
import type { WorkspaceLaunchpadNodeData } from "../workspaceLaunchpadUtils";
import { MODE_LABEL, fmtMoney, fmtR, signColor } from "../../LiveStrategies/liveStrategiesFormat";

const EDGE_HANDLE_CLASS =
  "!pointer-events-none !h-px !w-px !min-h-0 !min-w-0 !border-0 !bg-transparent !opacity-0";
const TYPE_LABEL_CLASS =
  "font-[family-name:var(--font-primary)] text-[9px] font-medium uppercase tracking-wide text-[color:var(--brand-tropical-surf)]";
const CARD_TITLE_CLASS =
  "font-[family-name:var(--font-primary)] text-[13px] font-medium leading-5 text-[color:var(--brand-tropical-surf)] dark:text-white";
const META_LABEL_CLASS =
  "font-[family-name:var(--font-primary)] text-[9px] font-medium uppercase tracking-wide text-muted-foreground";
const VALUE_CLASS = "truncate font-[family-name:var(--font-primary)] text-[12px] font-medium tabular-nums leading-snug";

const STATUS_COLOR: Record<string, string> = {
  running: "var(--tessera-pnl-positive)",
  paused: "var(--brand-tropical-surf)",
  error: "var(--brand-glacial-red)",
};

/** A policy version on Launchpad: what runs it (mode, status, result), or Launch for a policy of a
 *  Strategy Lab strategy (workspaceLaunchpadUtils). Clicking opens it on Launchpad. */
export function WorkspaceLaunchpadNode({ data, selected }: NodeProps): React.ReactElement {
  const flow = data as WorkspaceLaunchpadNodeData;
  const history = useHistory();
  const deps = flow.deployments ?? [];
  const launched = deps.length > 0;
  const lead = deps[0];
  const isSelected = Boolean(selected || flow.isSelected || flow.isParentSelected);
  const pnl = deps.reduce((a, d) => a + (d.summary?.pnl ?? 0), 0);
  const r = deps.reduce((a, d) => a + (d.summary?.r_total ?? 0), 0);
  const trades = deps.reduce((a, d) => a + (d.summary?.closed ?? 0), 0);
  const tone = launched ? STATUS_COLOR[lead.status] ?? "var(--muted-foreground)" : "var(--brand-tropical-surf)";

  const open = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (launched) history.push(`/launchpad?open=${encodeURIComponent(lead.id)}`);
    else if (flow.strategyId)
      // A policy of a Strategy Lab strategy: the wizard opens with it preloaded.
      history.push(
        `/launchpad?deploy_strategy=${encodeURIComponent(flow.strategyId)}&version=${encodeURIComponent(flow.policyVersionId)}`
      );
    else history.push("/launchpad");
  };

  return (
    <div
      data-workspace-node=""
      data-workspace-node-type="launchpad"
      data-workspace-launchpad={launched ? "running" : "idle"}
      className={cn("group relative", flow.focusFilterDimmed && "opacity-40")}
      style={{ width: WORKSPACE_EXECUTING_ACCOUNT_NODE_WIDTH, height: WORKSPACE_EXECUTING_ACCOUNT_NODE_HEIGHT }}
    >
      <div
        role="button"
        tabIndex={0}
        onClick={open}
        onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && open(e as unknown as React.MouseEvent)}
        className={cn(
          "nodrag absolute top-0 flex h-full cursor-pointer flex-col justify-between overflow-hidden rounded-lg border border-solid bg-card px-3 py-2.5 text-card-foreground shadow-sm transition-[box-shadow,border-color] hover:shadow-md dark:bg-sidebar dark:text-sidebar-foreground",
          isSelected && "ring-2 ring-primary/45 ring-offset-1 ring-offset-background"
        )}
        style={{
          left: WORKSPACE_EXECUTING_ACCOUNT_DIAMOND_OVERHANG,
          width: WORKSPACE_NODE_CARD_WIDTH,
          height: WORKSPACE_NODE_CARD_HEIGHT,
          paddingLeft: WORKSPACE_EXECUTING_ACCOUNT_DIAMOND_OVERHANG + 12,
          borderWidth: 1.5,
          borderColor: launched ? tone : "#6b7280",
          borderStyle: launched ? "solid" : "dashed",
        }}
        title={launched ? "Open on Launchpad" : "Launch this policy version"}
      >
        <div className="min-w-0">
          <p className={cn(TYPE_LABEL_CLASS, "m-0")}>Launchpad</p>
          <p className={cn(CARD_TITLE_CLASS, "m-0 mt-0.5 flex items-center gap-1.5 truncate whitespace-nowrap")}>
            {launched ? (
              <>
                <span className="relative flex size-2 shrink-0">
                  {lead.status === "running" ? (
                    <span className="absolute inline-flex size-full animate-ping rounded-full opacity-60" style={{ background: tone }} />
                  ) : null}
                  <span className="relative inline-flex size-2 rounded-full" style={{ background: tone }} />
                </span>
                <span className="truncate">
                  {deps.length > 1 ? `${deps.length} running` : MODE_LABEL[lead.mode]}
                  {lead.status === "paused" ? " · paused" : ""}
                </span>
              </>
            ) : (
              "Not launched"
            )}
          </p>
        </div>
        {launched ? (
          <div className="mt-2 grid grid-cols-3 gap-2">
            <div className="min-w-0">
              <p className={META_LABEL_CLASS}>PnL</p>
              <p className={VALUE_CLASS} style={{ color: signColor(pnl) }}>{fmtMoney(pnl)}</p>
            </div>
            <div className="min-w-0">
              <p className={META_LABEL_CLASS}>Total R</p>
              <p className={VALUE_CLASS} style={{ color: signColor(r) }}>{fmtR(r)}</p>
            </div>
            <div className="min-w-0">
              <p className={META_LABEL_CLASS}>Trades</p>
              <p className={cn(VALUE_CLASS, "text-muted-foreground")}>{trades}</p>
            </div>
          </div>
        ) : (
          <p className="m-0 mt-1 text-[11px] leading-snug text-muted-foreground">
            Run this policy on paper or your broker account.
          </p>
        )}
        <span className="mt-2 inline-flex items-center gap-1 text-[11px] font-medium text-[color:var(--brand-tropical-surf)]">
          {launched ? "Open on Launchpad" : "Launch"}
          <ArrowRight className="size-3 transition-transform group-hover:translate-x-0.5" />
        </span>
      </div>

      {/* Round badge (account nodes use a diamond): half in the card, half out. */}
      <div
        className="pointer-events-none absolute top-1/2 z-[1] flex -translate-y-1/2 items-center justify-center"
        style={{ left: 0, width: WORKSPACE_EXECUTING_ACCOUNT_DIAMOND_SIZE, height: WORKSPACE_EXECUTING_ACCOUNT_DIAMOND_SIZE }}
      >
        <div
          className={cn(
            "flex items-center justify-center rounded-full border border-solid bg-card shadow-sm dark:bg-sidebar",
            isSelected && "ring-2 ring-primary/45 ring-offset-1 ring-offset-background"
          )}
          style={{
            width: WORKSPACE_ACCOUNT_CARD_INNER_SIZE,
            height: WORKSPACE_ACCOUNT_CARD_INNER_SIZE,
            borderWidth: 1.5,
            borderColor: launched ? tone : "#6b7280",
            background: launched ? `color-mix(in oklab, ${tone} 10%, var(--card))` : undefined,
          }}
        >
          <Rocket className="size-9" style={{ color: launched ? tone : "var(--brand-tropical-surf)" }} />
        </div>
      </div>

      <Handle id="target" type="target" position={Position.Left} className={EDGE_HANDLE_CLASS}
        style={{ left: 0, top: "50%", transform: "translate(-50%, -50%)" }} isConnectable={false} />
      <Handle id="source" type="source" position={Position.Right} className={EDGE_HANDLE_CLASS}
        style={{ right: 0, top: "50%", transform: "translate(50%, -50%)" }} isConnectable={false} />
    </div>
  );
}
